import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import {
  Flame, Trophy, Sparkles, ArrowRight, Play, BookOpen, ClipboardList,
  TreePine, CalendarDays, FileText, AlertCircle, Loader2, Bell, Target,
  Wand2, MessageSquare, ScrollText, Quote, ChevronRight, CheckCircle2,
} from "lucide-react";
import { AICallout } from "@/components/makai/primitives";
import { MakaiInsightSlot } from "@/components/intelligence/makai-insight-slot";
import { levelFromXp } from "@/lib/achievements";

export const Route = createFileRoute("/learner/")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Learner — MAKAI" }] }),
  component: LearnerHome,
});

const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

const QUOTES = [
  { q: "Small steps every day become giant leaps in a year.", a: "MAKAI" },
  { q: "Your only limit is how badly you want to grow.", a: "MAKAI" },
  { q: "Learning is the one investment that always pays you back.", a: "MAKAI" },
  { q: "Show up today. Future you is watching.", a: "MAKAI" },
  { q: "The expert in anything was once a beginner.", a: "MAKAI" },
];

type SlotRow = { id?: string; start_time: string; end_time: string; room: string | null; subject: { name: string } | null; staff: { first_name: string | null; last_name: string | null } | null };
type AssignRow = { id: string; title: string; due_at: string | null; max_score: number | null; subject: { name: string } | null };
type ExamRow = { id: string; title: string; scheduled_at: string | null; subject: { name: string } | null };
type GradeRow = { id: string; score: number | null; max_score: number | null; created_at: string; assessment: { title: string | null; subject: { name: string | null } | null } | null };
type NotifRow = { id: string; title: string; body: string | null; created_at: string; read_at: string | null };
type LessonRow = { id: string; title: string; subject: { name: string | null } | null };

function LearnerHome() {
  const { ctx, loading, error } = useLearnerContext();
  const [slots, setSlots] = useState<SlotRow[]>([]);
  const [assigns, setAssigns] = useState<AssignRow[]>([]);
  const [exams, setExams] = useState<ExamRow[]>([]);
  const [grades, setGrades] = useState<GradeRow[]>([]);
  const [notifs, setNotifs] = useState<NotifRow[]>([]);
  const [recommended, setRecommended] = useState<LessonRow | null>(null);
  const [continueLesson, setContinueLesson] = useState<LessonRow | null>(null);
  const [att, setAtt] = useState({ present: 0, total: 0 });
  const [xp, setXp] = useState({ points: 0, streak: 0 });
  const [progress, setProgress] = useState({ done: 0, total: 0, weekDone: 0 });
  const [badgeCount, setBadgeCount] = useState(0);

  const quote = useMemo(() => QUOTES[new Date().getDate() % QUOTES.length], []);

  useEffect(() => {
    if (!ctx) return;
    const today = DAYS[new Date().getDay()];
    const weekAgo = new Date(Date.now() - 7 * 86400_000).toISOString();
    (async () => {
      const since30 = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
      const [s, a, ex, n, attData, xpRow, comps, lessonsTotal, badges, gr, recLesson, contLesson] = await Promise.all([
        ctx.streamId ? supabase.from("timetable_slots")
          .select("start_time, end_time, room, subject:subjects_catalog(name), staff:staff(first_name, last_name)")
          .eq("stream_id", ctx.streamId).eq("day", today).order("start_time") : Promise.resolve({ data: [] as SlotRow[] }),
        ctx.streamId ? supabase.from("assignments")
          .select("id, title, due_at, max_score, subject:subjects_catalog(name)")
          .eq("stream_id", ctx.streamId).gte("due_at", new Date().toISOString())
          .order("due_at").limit(5) : Promise.resolve({ data: [] as AssignRow[] }),
        ctx.streamId ? supabase.from("assessments")
          .select("id, title, scheduled_at, subject:subjects_catalog(name)")
          .eq("stream_id", ctx.streamId)
          .gte("scheduled_at", new Date().toISOString())
          .order("scheduled_at").limit(3) : Promise.resolve({ data: [] as ExamRow[] }),
        supabase.from("notifications").select("id, title, body, created_at, read_at")
          .eq("user_id", (await supabase.auth.getUser()).data.user?.id ?? "")
          .order("created_at", { ascending: false }).limit(4),
        supabase.from("attendance").select("status").eq("learner_id", ctx.learnerId).gte("date", since30),
        supabase.from("learner_xp").select("points, current_streak").eq("learner_id", ctx.learnerId).maybeSingle(),
        supabase.from("lesson_completions").select("id, status, updated_at").eq("learner_id", ctx.learnerId),
        ctx.schoolId
          ? supabase.from("lessons").select("id", { count: "exact", head: true }).eq("school_id", ctx.schoolId).eq("published", true)
          : Promise.resolve({ count: 0 }),
        supabase.from("learner_badges").select("id", { count: "exact", head: true }).eq("learner_id", ctx.learnerId),
        supabase.from("assessment_marks")
          .select("id, score, max_score, created_at, assessment:assessments(title, subject:subjects_catalog(name))")
          .eq("learner_id", ctx.learnerId)
          .order("created_at", { ascending: false }).limit(4),
        ctx.schoolId ? supabase.from("lessons")
          .select("id, title, subject:subjects_catalog(name)")
          .eq("school_id", ctx.schoolId).eq("published", true)
          .order("created_at", { ascending: false }).limit(1).maybeSingle() : Promise.resolve({ data: null }),
        ctx.schoolId ? supabase.from("lesson_completions")
          .select("lesson:lessons(id, title, subject:subjects_catalog(name)), updated_at, status")
          .eq("learner_id", ctx.learnerId).eq("status", "in_progress")
          .order("updated_at", { ascending: false }).limit(1).maybeSingle() : Promise.resolve({ data: null }),
      ]);
      setSlots((s.data as SlotRow[] | null) ?? []);
      setAssigns((a.data as AssignRow[] | null) ?? []);
      setExams((ex.data as ExamRow[] | null) ?? []);
      setNotifs((n.data as NotifRow[] | null) ?? []);
      setGrades((gr.data as GradeRow[] | null) ?? []);
      const rows = (attData.data ?? []) as { status: string }[];
      setAtt({ present: rows.filter((r) => r.status === "present").length, total: rows.length });
      setXp({ points: xpRow.data?.points ?? 0, streak: xpRow.data?.current_streak ?? 0 });
      const cdata = ((comps.data ?? []) as { status: string; updated_at: string }[]);
      const cdone = cdata.filter((c) => c.status === "completed").length;
      const wDone = cdata.filter((c) => c.status === "completed" && c.updated_at >= weekAgo).length;
      setProgress({ done: cdone, total: (lessonsTotal as { count?: number }).count ?? 0, weekDone: wDone });
      setBadgeCount((badges as { count?: number }).count ?? 0);
      setRecommended((recLesson as { data: LessonRow | null }).data ?? null);
      const cl = (contLesson as { data: { lesson: LessonRow | null } | null }).data;
      setContinueLesson(cl?.lesson ?? null);
    })();
  }, [ctx]);

  if (loading) {
    return (
      <AppShell role="learner">
        <div className="grid place-items-center py-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </AppShell>
    );
  }

  if (error || !ctx) {
    return (
      <AppShell role="learner">
        <Link
          to="/learner/onboarding"
          className="group block rounded-3xl border-2 border-dashed border-primary/40 bg-card p-10 text-center transition hover:border-primary hover:bg-primary/5"
        >
          <span className="mx-auto inline-grid h-14 w-14 place-items-center rounded-full bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
            <AlertCircle className="h-7 w-7" />
          </span>
          <h3 className="mt-4 font-display text-xl font-bold">No learner profile yet</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {error ?? "Tap here to set one up — pick your school and add your name to get started."}
          </p>
          <span className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-soft">
            Create my profile <ArrowRight className="h-4 w-4" />
          </span>
        </Link>
      </AppShell>
    );
  }

  const attPct = att.total ? Math.round((att.present / att.total) * 100) : 0;
  const progPct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const weekGoal = 5;
  const weekPct = Math.min(100, Math.round((progress.weekDone / weekGoal) * 100));
  const initials = `${ctx.firstName?.[0] ?? ""}${ctx.lastName?.[0] ?? ""}`.toUpperCase() || "L";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  // Daily mission: simple computed goal
  const missionGoal = 3;
  const missionDone = Math.min(missionGoal, progress.weekDone);
  const missionPct = Math.round((missionDone / missionGoal) * 100);

  return (
    <AppShell role="learner">
      {/* ───────────── Welcome Hero ───────────── */}
      <section className="relative overflow-hidden rounded-[28px] bg-[#0B1736] p-5 text-white shadow-[0_20px_60px_-20px_rgba(11,23,54,0.55)] sm:p-9">
        {/* ambient gold glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(circle, #C8A03C 0%, transparent 70%)" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-32 bottom-0 h-64 w-64 rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #C8A03C 0%, transparent 70%)" }}
        />

        <div className="relative grid min-w-0 gap-7 lg:grid-cols-[1.3fr_1fr] lg:items-center">
          <div className="min-w-0">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="relative shrink-0">
                <div
                  className="grid h-12 w-12 place-items-center rounded-2xl font-display text-base font-bold text-[#0B1736] ring-2 ring-[#C8A03C]/40 ring-offset-2 ring-offset-[#0B1736] sm:h-14 sm:w-14 sm:text-lg"
                  style={{ background: "linear-gradient(135deg,#E4C76A,#C8A03C)" }}
                >
                  {initials}
                </div>
                <span className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-full bg-[#0B1736] ring-2 ring-[#C8A03C]">
                  <Flame className="h-3 w-3 text-[#C8A03C]" />
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/60 sm:text-xs">{greeting}</div>
                <h1 className="mt-0.5 truncate font-display text-xl font-bold sm:text-3xl">
                  Welcome back, {ctx.firstName ?? "Learner"}
                </h1>
              </div>
            </div>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/70">
              {ctx.gradeLevel ? `${ctx.gradeLevel} · ` : ""}You're on a {xp.streak}-day streak. Keep the rhythm going today.
            </p>

            {/* Daily mission */}
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#C8A03C]/15 text-[#C8A03C]">
                    <Target className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold">Daily Learning Mission</div>
                    <div className="text-[11px] text-white/60">Complete {missionGoal} lessons today</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-bold text-[#C8A03C]">{missionDone}/{missionGoal}</div>
                </div>
              </div>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${missionPct}%`, background: "linear-gradient(90deg,#E4C76A,#C8A03C)" }}
                />
              </div>
            </div>
          </div>

          {/* Hero stats */}
          <div className="grid grid-cols-3 gap-3">
            <HeroStat label="XP" value={xp.points.toLocaleString()} icon={Sparkles} />
            <HeroStat label="Streak" value={`${xp.streak}d`} icon={Flame} />
            <HeroStat label="Badges" value={String(badgeCount)} icon={Trophy} />
          </div>

          {/* Level progress */}
          {(() => {
            const lv = levelFromXp(xp.points);
            const pct = Math.round(lv.progress * 100);
            return (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#C8A03C]/15 text-[#C8A03C]">
                      <Trophy className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="text-sm font-semibold">Level {lv.level} · {lv.title}</div>
                      <div className="text-[11px] text-white/60">
                        {Math.max(0, lv.span - lv.intoLevel).toLocaleString()} XP to Level {lv.level + 1}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg font-bold text-[#C8A03C]">{pct}%</div>
                    <div className="text-[10px] text-white/50">{lv.intoLevel}/{lv.span} XP</div>
                  </div>
                </div>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, background: "linear-gradient(90deg,#E4C76A,#C8A03C)" }}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* ───────────── Continue Learning + Recommended ───────────── */}
      <section className="mt-6 grid gap-4 lg:grid-cols-2">
        <PremiumCard className="bg-gradient-to-br from-[#0B1736] to-[#152551] text-white">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C8A03C]">
            <Play className="h-3.5 w-3.5" /> Continue learning
          </div>
          <h3 className="mt-3 font-display text-xl font-bold leading-snug">
            {continueLesson?.title ?? "Pick up a fresh lesson"}
          </h3>
          <p className="mt-1.5 text-sm text-white/70">
            {continueLesson?.subject?.name ?? "Tap below to dive in"}
          </p>
          <Link
            to="/learner/lessons" search={{ subject: undefined }}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#C8A03C] px-4 py-2.5 text-sm font-semibold text-[#0B1736] shadow-[0_8px_24px_-8px_rgba(200,160,60,0.6)] transition hover:brightness-110"
          >
            Resume <ArrowRight className="h-4 w-4" />
          </Link>
        </PremiumCard>

        <PremiumCard accent>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C8A03C]">
            <Wand2 className="h-3.5 w-3.5" /> AI recommended
          </div>
          <h3 className="mt-3 font-display text-xl font-bold leading-snug">
            {recommended?.title ?? "MAKAI is preparing your next lesson"}
          </h3>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {recommended?.subject?.name ?? "Personalised for your level"}
          </p>
          <Link
            to="/learner/tree"
            className="mt-5 inline-flex items-center gap-2 rounded-xl border border-[#C8A03C]/40 bg-[#C8A03C]/10 px-4 py-2.5 text-sm font-semibold text-[#8a6f29] transition hover:bg-[#C8A03C]/15"
          >
            Open lesson <ChevronRight className="h-4 w-4" />
          </Link>
        </PremiumCard>
      </section>

      {/* ───────────── MAKAI Insight (evidence-based, renders only when there's real data) ───────────── */}
      <section className="mt-6">
        <MakaiInsightSlot />
      </section>

      {/* ───────────── Stats: Streak / Weekly Ring / Attendance ───────────── */}
      <section className="mt-6 grid gap-4 sm:grid-cols-3">
        <PremiumCard>
          <StatHeader icon={Flame} label="Study Streak" />
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-display text-4xl font-bold tracking-tight">{xp.streak}</span>
            <span className="text-sm text-muted-foreground">days</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Don't break the chain — log in tomorrow.</p>
        </PremiumCard>

        <PremiumCard>
          <StatHeader icon={Target} label="Weekly Progress" />
          <div className="mt-3 flex items-center gap-4">
            <ProgressRing value={weekPct} />
            <div>
              <div className="font-display text-2xl font-bold">{progress.weekDone}<span className="text-base text-muted-foreground">/{weekGoal}</span></div>
              <div className="text-xs text-muted-foreground">lessons this week</div>
            </div>
          </div>
        </PremiumCard>

        <PremiumCard>
          <StatHeader icon={CheckCircle2} label="Attendance" />
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-display text-4xl font-bold tracking-tight">{attPct}<span className="text-xl text-muted-foreground">%</span></span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-[#C8A03C] transition-all duration-700" style={{ width: `${attPct}%` }} />
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{att.present} of {att.total} days · last 30 days</p>
        </PremiumCard>
      </section>

      {/* ───────────── Timetable + Homework ───────────── */}
      <section className="mt-6 grid gap-4 lg:grid-cols-2">
        <PremiumCard>
          <SectionTitle icon={CalendarDays} title="Today's timetable" hint={new Date().toLocaleDateString("en-UG", { weekday: "long", month: "short", day: "numeric" })} />
          <ol className="mt-4 space-y-2.5">
            {slots.length === 0 && <EmptyRow text="No classes scheduled today." />}
            {slots.slice(0, 5).map((s, i) => (
              <li key={i} className="group grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-2xl border border-border/60 bg-card/40 px-4 py-3 transition hover:border-[#C8A03C]/40 hover:bg-[#C8A03C]/5">
                <div className="font-display text-sm font-bold text-[#C8A03C]">{s.start_time.slice(0, 5)}</div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{s.subject?.name ?? "Free period"}</div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {s.staff ? `${s.staff.first_name ?? ""} ${s.staff.last_name ?? ""}`.trim() : "—"}{s.room ? ` · ${s.room}` : ""}
                  </div>
                </div>
                <div className="text-[11px] text-muted-foreground">{s.end_time.slice(0, 5)}</div>
              </li>
            ))}
          </ol>
        </PremiumCard>

        <PremiumCard>
          <SectionTitle icon={FileText} title="Homework due today" link={{ to: "/learner/assignments", label: "All" }} />
          <ul className="mt-4 space-y-2.5">
            {assigns.length === 0 && <EmptyRow text="No homework due. Enjoy the breather!" />}
            {assigns.slice(0, 4).map((a) => {
              const due = a.due_at ? new Date(a.due_at) : null;
              const days = due ? Math.ceil((due.getTime() - Date.now()) / 86400_000) : null;
              const urgent = (days ?? 99) <= 1;
              return (
                <li key={a.id} className="rounded-2xl border border-border/60 bg-card/40 px-4 py-3 transition hover:border-[#C8A03C]/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{a.title}</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">{a.subject?.name ?? "General"} · {a.max_score ?? 0} marks</div>
                    </div>
                    {days !== null && (
                      <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${urgent ? "bg-red-50 text-red-700" : "bg-[#C8A03C]/15 text-[#8a6f29]"}`}>
                        {days <= 0 ? "Today" : `${days}d`}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </PremiumCard>
      </section>

      {/* ───────────── Upcoming Exams + Recent Grades ───────────── */}
      <section className="mt-6 grid gap-4 lg:grid-cols-2">
        <PremiumCard>
          <SectionTitle icon={ScrollText} title="Upcoming exams" link={{ to: "/learner/quizzes", label: "All" }} />
          <ul className="mt-4 space-y-2.5">
            {exams.length === 0 && <EmptyRow text="No exams scheduled." />}
            {exams.map((e) => {
              const d = e.scheduled_at ? new Date(e.scheduled_at) : null;
              return (
                <li key={e.id} className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card/40 px-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{e.title}</div>
                    <div className="text-[11px] text-muted-foreground">{e.subject?.name ?? "General"}</div>
                  </div>
                  {d && (
                    <div className="shrink-0 text-right">
                      <div className="font-display text-sm font-bold">{d.toLocaleDateString("en-UG", { month: "short", day: "numeric" })}</div>
                      <div className="text-[10px] text-muted-foreground">{d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </PremiumCard>

        <PremiumCard>
          <SectionTitle icon={Trophy} title="Recent grades" link={{ to: "/learner/grades", label: "All" }} />
          <ul className="mt-4 space-y-2.5">
            {grades.length === 0 && <EmptyRow text="No grades yet." />}
            {grades.map((g) => {
              const pct = g.score != null && g.max_score ? Math.round((g.score / g.max_score) * 100) : null;
              const good = (pct ?? 0) >= 70;
              return (
                <li key={g.id} className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-2xl border border-border/60 bg-card/40 px-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{g.assessment?.title ?? "Assessment"}</div>
                    <div className="text-[11px] text-muted-foreground">{g.assessment?.subject?.name ?? "General"}</div>
                  </div>
                  <div className={`shrink-0 rounded-xl px-3 py-1.5 text-center font-display text-sm font-bold ${good ? "bg-emerald-50 text-emerald-700" : "bg-[#C8A03C]/15 text-[#8a6f29]"}`}>
                    {pct !== null ? `${pct}%` : "—"}
                  </div>
                </li>
              );
            })}
          </ul>
        </PremiumCard>
      </section>

      {/* ───────────── AI Motivation Quote ───────────── */}
      <section className="mt-6">
        <div className="relative overflow-hidden rounded-[28px] bg-[#0B1736] p-7 text-white sm:p-9">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-10 h-48 w-48 rounded-full opacity-30 blur-3xl"
            style={{ background: "radial-gradient(circle, #C8A03C 0%, transparent 70%)" }}
          />
          <Quote className="h-7 w-7 text-[#C8A03C]" />
          <p className="relative mt-3 max-w-2xl font-display text-xl font-medium leading-snug sm:text-2xl">
            "{quote.q}"
          </p>
          <div className="mt-3 text-xs uppercase tracking-[0.2em] text-white/50">— {quote.a}, your AI coach</div>
        </div>
      </section>

      {/* ───────────── Quick Actions ───────────── */}
      <section className="mt-6">
        <h2 className="px-1 font-display text-lg font-bold">Quick actions</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickAction icon={Sparkles} label="Ask MAKAI" sub="AI tutor" to="/learner/tutor" />
          <QuickAction icon={ClipboardList} label="Take a quiz" sub="Test yourself" to="/learner/quizzes" />
          <QuickAction icon={TreePine} label="Learning Tree" sub="Roadmap" to="/learner/tree" />
          <QuickAction icon={BookOpen} label="Library" sub="Resources" to="/learner/library" />
        </div>
      </section>

      {/* ───────────── Recent Notifications ───────────── */}
      <section className="mt-6 mb-2">
        <PremiumCard>
          <SectionTitle icon={Bell} title="Recent notifications" />
          <ul className="mt-4 space-y-2.5">
            {notifs.length === 0 && <EmptyRow text="You're all caught up." />}
            {notifs.map((n) => (
              <li key={n.id} className="flex items-start gap-3 rounded-2xl border border-border/60 bg-card/40 px-4 py-3">
                <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${n.read_at ? "bg-muted-foreground/30" : "bg-[#C8A03C]"}`} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{n.title}</div>
                  {n.body && <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{n.body}</p>}
                  <div className="mt-1 text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </li>
            ))}
          </ul>
        </PremiumCard>
      </section>

      {/* Ask MAKAI — contextual prompts for this learner */}
      <section className="mt-6">
        <AICallout
          description="I can plan your day, find your weak spots and pick the right next lesson."
          suggestions={[
            "What should I study today?",
            "Where am I falling behind?",
            "Plan my revision for this week",
            "Pick a quick 10-minute lesson",
          ]}
        />
      </section>
    </AppShell>
  );
}

/* ──────────────────────── Subcomponents ──────────────────────── */

function PremiumCard({ children, className = "", accent = false }: { children: React.ReactNode; className?: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-[24px] border bg-card p-5 shadow-[0_2px_12px_-4px_rgba(11,23,54,0.08)] transition duration-300 hover:shadow-[0_12px_36px_-12px_rgba(11,23,54,0.15)] sm:p-6 ${accent ? "border-[#C8A03C]/30" : "border-border/70"} ${className}`}
    >
      {children}
    </div>
  );
}

function HeroStat({ label, value, icon: Icon }: { label: string; value: string; icon: typeof Flame }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-3.5 backdrop-blur-sm">
      <Icon className="h-3.5 w-3.5 text-[#C8A03C]" />
      <div className="mt-2 font-display text-xl font-bold leading-none">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-white/60">{label}</div>
    </div>
  );
}

function StatHeader({ icon: Icon, label }: { icon: typeof Flame; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="grid h-8 w-8 place-items-center rounded-xl bg-[#C8A03C]/15 text-[#8a6f29]">
        <Icon className="h-4 w-4" />
      </span>
      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
    </div>
  );
}

function SectionTitle({ icon: Icon, title, hint, link }: { icon: typeof Flame; title: string; hint?: string; link?: { to: string; label: string } }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <Icon className="h-4 w-4 shrink-0 text-[#C8A03C]" />
        <h3 className="truncate font-display text-base font-bold">{title}</h3>
      </div>
      {hint && <span className="shrink-0 text-[11px] text-muted-foreground">{hint}</span>}
      {link && (
        <Link to={link.to} className="shrink-0 text-xs font-semibold text-[#8a6f29] hover:underline">
          {link.label}
        </Link>
      )}
    </div>
  );
}

function ProgressRing({ value }: { value: number }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  return (
    <div className="relative grid h-16 w-16 shrink-0 place-items-center">
      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} stroke="currentColor" strokeWidth="6" fill="none" className="text-muted" />
        <circle
          cx="32" cy="32" r={r}
          stroke="#C8A03C" strokeWidth="6" fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={off}
          style={{ transition: "stroke-dashoffset 900ms cubic-bezier(.2,.8,.2,1)" }}
        />
      </svg>
      <span className="font-display text-sm font-bold">{value}%</span>
    </div>
  );
}

function QuickAction({ icon: Icon, label, sub, to }: { icon: typeof Flame; label: string; sub: string; to: string }) {
  return (
    <Link
      to={to}
      className="group flex items-center justify-between gap-3 rounded-2xl border border-border/70 bg-card p-4 transition hover:-translate-y-0.5 hover:border-[#C8A03C]/40 hover:shadow-[0_12px_28px_-12px_rgba(200,160,60,0.25)]"
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#0B1736] text-[#C8A03C] transition group-hover:scale-105">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{label}</div>
          <div className="truncate text-[11px] text-muted-foreground">{sub}</div>
        </div>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-[#C8A03C]" />
    </Link>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <div className="rounded-2xl border border-dashed border-border/60 px-4 py-5 text-center text-xs text-muted-foreground">{text}</div>;
}

/* ──────────────────────── Re-exports (back-compat) ──────────────────────── */

export function tintBg(c: "primary" | "gold" | "sky" | "clay") {
  return c === "primary" ? "bg-primary-soft text-primary"
    : c === "gold" ? "bg-gold/20 text-gold-foreground"
    : c === "sky" ? "bg-sky/15 text-sky"
    : "bg-orange-100 text-orange-700";
}
export const tintPill = tintBg;
export function SectionHeader({ title, to }: { title: string; to: string }) {
  return (
    <div className="flex items-end justify-between">
      <h2 className="font-display text-lg font-bold">{title}</h2>
      <Link to={to} className="text-sm font-semibold text-primary hover:underline">See all</Link>
    </div>
  );
}
export function EmptyCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/60 p-6 text-center">
      <div className="font-semibold">{title}</div>
      <div className="mt-1 text-xs text-muted-foreground">{body}</div>
    </div>
  );
}

export { MessageSquare };
